import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/src/shared/lib/utils';
import { LoadingSpinner } from '@/src/shared/ui/spinner/LoadingSpinner';

/**
 * TEXT BUTTON COMPONENT
 *
 * Текстовая кнопка дизайн-системы Hummii. Кнопка без фона и рамки.
 *
 * Состояния:
 * 1. Default: text-accent-1 (зелёный)
 * 2. Hover: text-accent-secondary + underline
 * 3. Pressed: text-text-primary
 * 4. Loading: text-text-secondary + spinner
 * 5. Disabled: text-text-tertiary
 *
 * @example
 * ```tsx
 * <TextButton>Click me</TextButton>
 * <TextButton isLoading>Loading...</TextButton>
 * <TextButton disabled>Disabled</TextButton>
 * <TextButton size="sm">Small button</TextButton>
 * ```
 */

interface TextButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Состояние загрузки
   * Показывает спиннер и делает кнопку неактивной
   */
  isLoading?: boolean;

  /**
   * Размер кнопки
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Растянуть на всю ширину родителя
   */
  fullWidth?: boolean;
}

export const TextButton = forwardRef<HTMLButtonElement, TextButtonProps>(
  (
    {
      className,
      children,
      disabled,
      isLoading = false,
      size = 'md',
      fullWidth = false,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-6 py-3 text-lg',
    };

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={cn(
          // Базовые стили
          'relative inline-flex items-center justify-center gap-2',
          'transition-all duration-200 ease-in-out',
          'font-medium',

          // Размер
          sizeClasses[size],

          // Фон и border - нет
          'bg-transparent border-none',

          // Default состояние
          'text-accent-1',

          // Hover состояние
          'hover:enabled:text-accent-secondary hover:enabled:underline',

          // Active/Pressed состояние
          'active:enabled:text-text-primary',

          // Loading состояние
          isLoading && '!text-text-secondary',

          // Disabled состояние
          'disabled:text-text-tertiary disabled:cursor-not-allowed',

          // Full width
          fullWidth && 'w-full',

          // Focus visible
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-1 focus-visible:ring-offset-2 rounded',

          className
        )}
        {...props}
      >
        {isLoading && (
          <LoadingSpinner size="sm" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
        )}
        <span className={cn(isLoading && 'invisible')}>{children}</span>
      </button>
    );
  }
);

TextButton.displayName = 'TextButton';
